import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  CardHeader,
  Tabs,
  Tab,
} from '@mui/material';
import Loader from '../../../components/Loader';
import RefreshIcon from '@mui/icons-material/Refresh';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import { CompanyLicenseRenewals, BrokerLicenseRenewals } from '../../../client/services';
import moment from 'moment';
import useToggle from '../../../hooks/useToggle';
import LicenseManagementModal from '../../Users/LicenseManagementModal';
import { Users } from '../../../store/users/api/interfaces';
import { toast } from 'react-toastify';

const DashboardLicenseRenewalTable = () => {
  const [renewalRequests, setRenewalRequests] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [actionUser, setActionUser] = useState<Users>();
  const [selectedRole, setSelectedRole] = useState<'company' | 'broker'>('company');
  const {
    toggle: toggleLicense,
    onOpen: onOpenLicense,
    onClose: onCloseLicense,
  } = useToggle();

  useEffect(() => {
    fetchRenewalRequests();
  }, [limit, skip, selectedRole]);

  const fetchRenewalRequests = async () => {
    try {
      setIsLoading(true);
      const RenewalService = selectedRole === 'broker' ? BrokerLicenseRenewals : CompanyLicenseRenewals;
      
      const response = await RenewalService.find({
        query: {
          status: 'pending',
          limit: limit,
          skip: skip,
        },
      });

      if (response?.data) {
        setRenewalRequests(response.data);
        setTotalRecords(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching renewal requests:', error);
      toast.error('Failed to load renewal requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    const newSkip = limit * newPage;
    setSkip(newSkip);
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newLimit = +event.target.value;
    setLimit(newLimit);
    setSkip(0);
    setCurrentPage(0);
  };

  const handleRefresh = () => {
    fetchRenewalRequests();
  };

  const handleRoleChange = (event: React.SyntheticEvent, newValue: 'company' | 'broker') => {
    setSelectedRole(newValue);
    setSkip(0);
    setCurrentPage(0);
  };

  const handleActionUpdate = (renewal: any): void => {
    // Convert renewal data to Users format for the modal
    const user: Users = {
      id: selectedRole === 'broker' 
        ? (renewal.broker?.id || renewal.brokerId)
        : (renewal.company?.id || renewal.companyId),
      name: selectedRole === 'broker'
        ? (renewal.broker?.name || 'Unknown')
        : (renewal.company?.name || 'Unknown'),
      email: selectedRole === 'broker'
        ? (renewal.broker?.email || '')
        : (renewal.company?.email || ''),
      userRole: selectedRole,
      isApproved: true,
      activeState: true,
    } as Users;
    
    setActionUser(user);
    onOpenLicense();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Card>
      <CardHeader
        title={'License Renewal Requests'}
        action={
          <IconButton
            aria-label='settings'
            onClick={() => handleRefresh()}
          >
            <RefreshIcon />
          </IconButton>
        }
        sx={{ mb: 2 }}
      />
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={selectedRole}
          onChange={handleRoleChange}
          aria-label="license renewal type tabs"
        >
          <Tab label="Companies" value="company" />
          <Tab label="Brokers" value="broker" />
        </Tabs>
      </Box>

        <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
          <Table size={'medium'} sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{selectedRole === 'broker' ? 'Broker Name' : 'Company Name'}</TableCell>
                <TableCell align='center'>{'Email'}</TableCell>
                <TableCell align='center'>{'Request Date'}</TableCell>
                <TableCell align='center'>{'Current License Expires'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : renewalRequests?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                renewalRequests?.map((renewal) => (
                  <TableRow key={renewal.id}>
                    <TableCell align='center'>
                      {selectedRole === 'broker'
                        ? (renewal.broker?.name || '-')
                        : (renewal.company?.name || '-')}
                    </TableCell>
                    <TableCell align='center'>
                      {selectedRole === 'broker'
                        ? (renewal.broker?.email || '-')
                        : (renewal.company?.email || '-')}
                    </TableCell>
                    <TableCell align='center'>
                      {moment(renewal.createdAt).format('YYYY-MM-DD')}
                    </TableCell>
                    <TableCell align='center'>
                      {renewal.currentLicense?.expirationDate
                        ? moment(renewal.currentLicense.expirationDate).format('YYYY-MM-DD')
                        : '-'}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={getStatusColor(renewal.status) as any}
                      >
                        {renewal.status}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton
                        aria-label='manage-license'
                        onClick={() => handleActionUpdate(renewal)}
                        color='primary'
                      >
                        <Iconify icon={'eva:edit-outline'} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>

      {toggleLicense && actionUser && (
        <LicenseManagementModal
          toggle={toggleLicense}
          onClose={() => {
            onCloseLicense();
            setActionUser(undefined);
          }}
          actionUser={actionUser}
          onApprovalSuccess={() => {
            // Immediately refresh the table when renewal is approved
            fetchRenewalRequests();
          }}
          onRejectionSuccess={() => {
            // Immediately refresh the table when renewal is rejected
            fetchRenewalRequests();
          }}
        />
      )}
    </Card>
  );
};

export default DashboardLicenseRenewalTable;

